/*
 * Amazon-style mobile drawer.
 *
 * Panels are all in the DOM from the start; moving between them sets a class
 * and the transform does the rest. The element keeps its own small stack of
 * visited panels so Back returns the way the reader came rather than always
 * jumping to the root.
 */
class PbMenuDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[id="pb-md-panel"]');
    this.overlay = this.querySelector('[data-pb-md-overlay]');
    this.trigger = this.querySelector('[data-pb-md-open]');
    this.viewport = this.querySelector('[data-pb-md-viewport]');
    if (!this.panel || !this.trigger) return;

    this.views = new Map();
    this.querySelectorAll('[data-pb-md-view]').forEach((v) => {
      this.views.set(v.dataset.pbMdView, v);
    });

    this.stack = ['root'];
    this.isOpen = false;
    this.lastFocused = null;

    /*
     * Move the overlay and the panel to the end of <body>.
     *
     * The header sets a z-index of its own, which makes it a stacking
     * context, and a fixed child cannot escape one however high its z-index
     * is - the whole header is painted as a single unit against the banner
     * below it. Reparenting to the document root puts the drawer in the
     * top-level context, where its z-index actually counts.
     */
    if (this.overlay.parentElement !== document.body) {
      document.body.append(this.overlay, this.panel);
    }

    this.trigger.addEventListener('click', () => this.open());
    this.overlay?.addEventListener('click', () => this.close());
    // Scoped to the panel, not to this element: the panel now lives on body.
    this.panel.querySelectorAll('[data-pb-md-close]').forEach((b) =>
      b.addEventListener('click', () => this.close())
    );

    this.panel.addEventListener('click', (e) => {
      const to = e.target.closest('[data-pb-md-to]');
      if (!to) return;
      e.preventDefault();
      const target = to.dataset.pbMdTo;
      if (target === 'root' && this.stack.length > 1) {
        this.back();
      } else {
        this.go(target);
      }
    });

    this.onKeydown = this.onKeydown.bind(this);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    this.unlockScroll();
    // The panel was reparented to body, so it does not leave with this element.
    this.overlay?.remove();
    this.panel?.remove();
  }

  /* Open / close ---------------------------------------------------------- */

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.lastFocused = document.activeElement;

    this.overlay.hidden = false;
    this.panel.hidden = false;
    // Two frames: the element has to be laid out unhidden before the
    // transform can animate, or it simply appears in place.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.overlay.setAttribute('data-open', '');
        this.panel.setAttribute('data-open', '');
      })
    );

    this.trigger.setAttribute('aria-expanded', 'true');
    this.lockScroll();
    document.addEventListener('keydown', this.onKeydown);

    const first = this.panel.querySelector('a, button');
    if (first) setTimeout(() => first.focus({ preventScroll: true }), 60);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.overlay.removeAttribute('data-open');
    this.panel.removeAttribute('data-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.unlockScroll();
    document.removeEventListener('keydown', this.onKeydown);

    const done = () => {
      if (this.isOpen) return;
      this.panel.hidden = true;
      this.overlay.hidden = true;
      this.reset();
    };
    this.panel.addEventListener('transitionend', done, { once: true });
    // transitionend does not fire when motion is reduced away.
    setTimeout(done, 400);

    this.lastFocused?.focus?.({ preventScroll: true });
  }

  /* Panel navigation ------------------------------------------------------ */

  go(name) {
    const next = this.views.get(name);
    const currentName = this.stack[this.stack.length - 1];
    if (!next || name === currentName) return;

    const current = this.views.get(currentName);
    current?.classList.remove('pb-md__view--active');
    current?.classList.add('pb-md__view--behind');
    current?.setAttribute('aria-hidden', 'true');

    next.classList.remove('pb-md__view--behind');
    next.classList.add('pb-md__view--active');
    next.removeAttribute('aria-hidden');
    next.scrollTop = 0;

    this.stack.push(name);
    const focusable = next.querySelector('a, button');
    if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 260);
  }

  back() {
    if (this.stack.length < 2) return;
    const leaving = this.views.get(this.stack.pop());
    const target = this.views.get(this.stack[this.stack.length - 1]);

    leaving?.classList.remove('pb-md__view--active');
    leaving?.setAttribute('aria-hidden', 'true');

    target?.classList.remove('pb-md__view--behind');
    target?.classList.add('pb-md__view--active');
    target?.removeAttribute('aria-hidden');

    const focusable = target?.querySelector('a, button');
    if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 260);
  }

  reset() {
    this.stack = ['root'];
    this.views.forEach((v, name) => {
      v.classList.toggle('pb-md__view--active', name === 'root');
      v.classList.remove('pb-md__view--behind');
      if (name === 'root') {
        v.removeAttribute('aria-hidden');
      } else {
        v.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /* Keyboard and scrolling ------------------------------------------------ */

  onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (this.stack.length > 1) {
        this.back();
      } else {
        this.close();
      }
      return;
    }
    if (e.key !== 'Tab') return;

    // Keep Tab inside the open drawer.
    const active = this.views.get(this.stack[this.stack.length - 1]);
    const items = [
      ...this.panel.querySelectorAll('.pb-md__bar a, .pb-md__bar button'),
      ...(active ? active.querySelectorAll('a, button') : []),
    ].filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  lockScroll() {
    this.scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.width = '100%';
  }

  unlockScroll() {
    if (document.body.style.position !== 'fixed') return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.scrollY || 0);
  }
}

if (!customElements.get('pb-menu-drawer')) {
  customElements.define('pb-menu-drawer', PbMenuDrawer);
}
